import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, Field, useFormState, useForm } from 'react-final-form';
import { Row, Col, Button, message, Form as AntForm } from 'antd';
import { fromInvoiceConfirmFields } from '../../constants/forms/formInvoiceConfirm';
import { TextField, DateField } from '../../atoms';
import { FormActions, FileUpload } from '../../components';
import { getInvoiceForConfirm } from '../../reducers/invoiceSearchReducer';
import { invoiceFields } from '../../constants/invoice';
import { FORM_INVOICE_CONFIRM } from '../../constants/formKeys';
import { downloadFileByPostURL } from '../../utils/files';
import { getLinkInvoiceExport } from '../../utils/gotoLink';
import isEqual from 'lodash/isEqual';
import validateSchema from './validateSchema';
import FormStateToRedux from '../FormStateToRedux';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';
import { FORMAT_DEFAULT, FORMAT_DEFAULT_DATE } from '../../utils/dates';
import dayjs from '../../utils/dayjs';

const FormItem = AntForm.Item;

// Inner component to access form state and methods via hooks
const InvoiceConfirmFields = ({ t, showTermsModal, initialValues }) => {
  const { values } = useFormState({
    subscription: { values: true },
  });
  const form = useForm();

  const file = values?.[fromInvoiceConfirmFields.FILE];

  const handleFileChangeInner = (info) => {
    if (info.file.status === 'done') {
      form.change(fromInvoiceConfirmFields.FILE, info.file.response);
    } else if (info.file.status === 'removed') {
      form.change(fromInvoiceConfirmFields.FILE, null);
    }
  };

  const handleBeforeUploadInner = () => {
    if (file) {
      message.error(t('confirm.uploadError'));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    // Touch fields by focusing and blurring
    form.focus(invoiceFields.NUMBER);
    form.blur(invoiceFields.NUMBER);
    form.focus(invoiceFields.DATE);
    form.blur(invoiceFields.DATE);

    // Get current valid state (not stale from previous render)
    const { valid: isValid } = form.getState();
    if (isValid) {
      showTermsModal();
    }
  };

  const handleDownloadClick = () => {
    const invoiceId = initialValues?.[invoiceFields.ID];
    downloadFileByPostURL(
      getLinkInvoiceExport(invoiceId),
      {},
      `invoice-${invoiceId}.pdf`,
      'get'
    );
  };

  return (
    <>
      <Row gutter={32}>
        <Col span={12}>
          <Field
            name={invoiceFields.NUMBER}
            label={t('confirm.number')}
            component={TextField}
            required
          />
        </Col>
        <Col span={12}>
          <Field
            className="date-field"
            name={invoiceFields.DATE}
            label={t('confirm.date')}
            component={DateField}
            required
          />
        </Col>
      </Row>
      <Row gutter={32}>
        <Col span={24}>
          <Field
            name={invoiceFields.REFERENCE_NUMBER}
            label={t('confirm.referenceNumber')}
            component={TextField}
          />
        </Col>
      </Row>
      <Row gutter={32}>
        <Col span={12}>
          <FormItem layout="vertical" label={t('confirm.file')}>
            <FileUpload
              t={t}
              onChange={handleFileChangeInner}
              beforeUpload={handleBeforeUploadInner}
            />
          </FormItem>
        </Col>
      </Row>
      <FormActions>
        <Button onClick={handleDownloadClick}>
          {t('confirm.downloadBtn')}
        </Button>
        <Button onClick={handleNext}>{t('confirm.nextBtn')}</Button>
      </FormActions>
    </>
  );
};

InvoiceConfirmFields.propTypes = {
  t: PropTypes.func.isRequired,
  showTermsModal: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
};

export class InvoiceConfirm extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    showTermsModal: PropTypes.func.isRequired,
    initialValues: PropTypes.object,
  };

  onSubmit = () => {};

  render() {
    const { t, showTermsModal, initialValues } = this.props;

    const validate = makeRffValidateFromLegacy(
      validateSchema,
      this.props,
      initialValues
    );

    return (
      <Form
        onSubmit={this.onSubmit}
        initialValues={initialValues}
        initialValuesEqual={isEqual}
        validate={validate}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <FormStateToRedux form={FORM_INVOICE_CONFIRM} />
            <InvoiceConfirmFields
              t={t}
              showTermsModal={showTermsModal}
              initialValues={initialValues}
            />
          </form>
        )}
      />
    );
  }
}

const mapStateToProps = (state) => {
  const invoiceData = getInvoiceForConfirm(state);
  const data = invoiceData?.toJS ? invoiceData.toJS() : invoiceData;

  // Strip time from date if present (server sends datetime, expects date only)
  const dateValue = data?.[invoiceFields.DATE];
  const parsed = dayjs(dateValue, FORMAT_DEFAULT, true);
  if (parsed.isValid()) {
    data[invoiceFields.DATE] = parsed.format(FORMAT_DEFAULT_DATE);
  }

  return {
    initialValues: data,
  };
};

export default connect(mapStateToProps)(InvoiceConfirm);
